// Interactive tutorial arena — a tiny scripted playground that teaches
// Chad (circle attack) mechanics hands-on instead of via text.
//
// Lives client-side only. No PartyKit, no SoloStub — the dummies are
// dumb THREE sprites with local HP, zone markers are glowing rings, and
// the stage machine lives in `src/ui/tutorial_guide.ts`. When the
// tutorial resolves, the arena is torn down and we fall through to the
// normal auto-queue flow.
//
// Zones are laid out on a 30×30u patch south of the lobby plaza so the
// player doesn't accidentally walk into the matchmaker / portal / bed
// during the scripted walk-around.

import * as THREE from 'three';

export type DummyKind = 'attack' | 'mog' | 'leap' | 'virginRing' | 'virginUlt';

export type TutorialZoneId = 'attack' | 'mog' | 'leap' | 'virginRing' | 'virginUlt';

export type Dummy = {
  id: number;
  kind: DummyKind;
  group: THREE.Group;
  pos: THREE.Vector3;
  hp: number;
  hpMax: number;
  regenPerSec: number;
  dead: boolean;
};

export type TutorialArenaUpdateOpts = {
  /** Player position — required when the virginUlt bot is alive so its
   *  chase-edge AI can read where you are. Also drives the stage-4
   *  Chad bot's damage-aura check. */
  playerPos?: THREE.Vector3;
  /** Movement-speed multiplier applied to the virginUlt bot. 1.0 =
   *  normal, < 1 = slowed (Ultra Instinct window). */
  ultraSlow?: number;
  /** Player hood state — when true, the stage-4 Chad bot can't see
   *  the player and stops dealing aura damage. The whole point of
   *  the 👻 VANISH stage. */
  playerHooded?: boolean;
};

export type TutorialArenaUpdateResult = {
  /** HP the stage-4 Chad bot drained from the player this tick (0 if
   *  player was hooded, out of range, or stage 4 isn't active). The
   *  caller decrements local.hp by this and renders the hit-flash. */
  playerDamage: number;
};

export type TutorialArenaHandle = {
  group: THREE.Group;
  dummies: Dummy[];
  zones: Record<TutorialZoneId, THREE.Vector3>;
  spawn: THREE.Vector3;
  update: (dt: number, opts?: TutorialArenaUpdateOpts) => TutorialArenaUpdateResult;
  tryHitCircle: (x: number, z: number, radius: number) => Dummy[];
  hitDummy: (id: number, dmg: number) => { killed: boolean; leeched: number };
  revealVirginBranch: () => void;
  activateUltraBot: () => void;
  dispose: () => void;
};

// Arena layout — placed south of the plaza entrance. Zones flow
// chronologically west → east so the player progresses naturally:
// spawn → 1 attack → 2 mog → 3 photo bomb → 4 virgin ring → 5 ultra.
const SPAWN = new THREE.Vector3(-4, 0, 38);
const ZONES: Record<TutorialZoneId, THREE.Vector3> = {
  attack:     new THREE.Vector3(  6, 0, 38),
  mog:        new THREE.Vector3( 14, 0, 44),
  leap:       new THREE.Vector3( 20, 0, 32),
  virginRing: new THREE.Vector3( 28, 0, 38),
  // virginUlt deliberately pushed far past the other zones — the
  // chase-edge bot roams within its own sandbox so the player can't
  // accidentally complete stage 5 while on stage 4.
  virginUlt:  new THREE.Vector3( 50, 0, 38),
};

export function buildTutorialArena(parent: THREE.Object3D): TutorialArenaHandle {
  const group = new THREE.Group();
  parent.add(group);

  // Ground plate — widened further east so the virginUlt bot's
  // sandbox at x≈50 stays on the plate while it runs around.
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(80, 26),
    new THREE.MeshStandardMaterial({
      color: 0x1f2937,
      emissive: 0x0f172a,
      emissiveIntensity: 0.2,
      roughness: 0.95,
    }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(22, 0.02, 38);
  group.add(ground);

  // Zone markers — chronological 1..5. The old "MOVE" zone was
  // dropped: players already learned to walk by reaching the tutorial
  // arena, and the extra step felt redundant.
  addZoneMarker(group, ZONES.attack,     0xef4444, '1 · ATTACK');
  addZoneMarker(group, ZONES.mog,        0xfb923c, '2 · MOG');
  addZoneMarker(group, ZONES.leap,       0x38bdf8, '3 · 📸 PHOTO BOMB');
  // Virgin branch zones — hidden at start, revealed after stage 3.
  const virginRingMarker = addZoneMarker(group, ZONES.virginRing, 0x60a5fa, '4 · 👻 VANISH');
  const virginUltMarker  = addZoneMarker(group, ZONES.virginUlt,  0x818cf8, '5 · 🐌 ULTRA INSTINCT');
  virginRingMarker.visible = false;
  virginUltMarker.visible = false;

  // Virgin range demo — a static "ghost" at the north edge of the
  // arena with the donut ring (4..5u) drawn on the ground + a label.
  // Lets the Chad-forced tutorial player see what Virgin's attack
  // shape looks like so they understand both factions before queueing.
  addVirginRangeDemo(group, new THREE.Vector3(2, 0, 30));

  // Dummy NPCs.
  const dummies: Dummy[] = [];
  let nextId = 1;
  function spawnDummy(kind: DummyKind, x: number, z: number, hpMax: number, regenPerSec = 0): Dummy {
    const d: Dummy = {
      id: nextId++,
      kind,
      group: makeDummyMesh(kind),
      pos: new THREE.Vector3(x, 0, z),
      hp: hpMax,
      hpMax,
      regenPerSec,
      dead: false,
    };
    d.group.position.copy(d.pos);
    group.add(d.group);
    dummies.push(d);
    return d;
  }
  // 3 attack dummies clustered around the attack zone.
  spawnDummy('attack', ZONES.attack.x - 2, ZONES.attack.z + 0.5, 40);
  spawnDummy('attack', ZONES.attack.x + 0, ZONES.attack.z - 1.5, 40);
  spawnDummy('attack', ZONES.attack.x + 2, ZONES.attack.z + 1.5, 40);
  // 1 mog dummy — higher HP + regen so the player must lifesteal
  // repeatedly to beat regen.
  spawnDummy('mog', ZONES.mog.x, ZONES.mog.z, 80, 8);
  // 1 leap dummy — far away; player must use Q ult to reach/damage it.
  spawnDummy('leap', ZONES.leap.x, ZONES.leap.z, 60);
  // Virgin branch dummies — spawn now but hide until stage 5 reveals
  // them. Hidden via the group's visible flag; the main tick already
  // skips damage on dead=true so visible=false + dead=false renders
  // nothing while stage 4 is active.
  const virginRingDummy = spawnDummy('virginRing', ZONES.virginRing.x, ZONES.virginRing.z, 60);
  const virginUltDummy  = spawnDummy('virginUlt',  ZONES.virginUlt.x,  ZONES.virginUlt.z,  80);
  virginRingDummy.group.visible = false;
  virginUltDummy.group.visible = false;

  // Stage 4 (👻 VANISH) damage-aura visual — mirrors the in-match
  // Chad attack circle (filled disc + bright outline ring) so the
  // player reads "this is the same threat they'll face in real
  // matches". Two meshes parented to the dummy group so visibility
  // tracks the bot itself.
  const VIRGIN_HOOD_AURA_RADIUS = 4.0; // matches COMBAT.CHAD_CIRCLE_R
  // Filled translucent disc — the chad attack circle's "hot zone".
  const dangerDisc = new THREE.Mesh(
    new THREE.CircleGeometry(VIRGIN_HOOD_AURA_RADIUS, 48),
    new THREE.MeshBasicMaterial({
      color: 0xef4444, transparent: true, opacity: 0.32,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    }),
  );
  dangerDisc.rotation.x = -Math.PI / 2;
  dangerDisc.visible = false;
  virginRingDummy.group.add(dangerDisc);
  dangerDisc.position.set(0, 0.04, 0);
  // Bright outline ring on top of the disc so the boundary reads
  // crisp from any angle.
  const dangerRing = new THREE.Mesh(
    new THREE.RingGeometry(VIRGIN_HOOD_AURA_RADIUS - 0.16, VIRGIN_HOOD_AURA_RADIUS + 0.04, 64),
    new THREE.MeshBasicMaterial({
      color: 0xff6b6b, transparent: true, opacity: 0.95, side: THREE.DoubleSide,
    }),
  );
  dangerRing.rotation.x = -Math.PI / 2;
  dangerRing.visible = false;
  virginRingDummy.group.add(dangerRing);
  dangerRing.position.set(0, 0.06, 0);

  let tSec = 0;
  // Chase-edge bot stays PUT until stage 5 explicitly activates it.
  // Otherwise the player finishes stage 4 (Virgin ring) and the bot
  // is already visible + roaming around the arena, risking an
  // accidental hit that completes stage 5 early.
  let ultraBotActive = false;
  return {
    group,
    dummies,
    zones: ZONES,
    spawn: SPAWN,
    update(dt: number, opts?: TutorialArenaUpdateOpts): TutorialArenaUpdateResult {
      tSec += dt;
      const playerPos = opts?.playerPos;
      const ultraSlow = opts?.ultraSlow ?? 1;
      const playerHooded = opts?.playerHooded ?? false;
      let playerDamage = 0;
      // Stage-4 chad-bot aura. Within HOOD_AURA_R of the dummy AND
      // not hooded → dummy drains HOOD_AURA_DPS hp/sec. Capped so it
      // never lethals the player even if they refuse to hood.
      const HOOD_AURA_R   = VIRGIN_HOOD_AURA_RADIUS;
      const HOOD_AURA_R2  = HOOD_AURA_R * HOOD_AURA_R;
      // 35 dps over the ~99 hp gap from full → 1 = ~2.8s to fully
      // bleed. Punchy enough to FEEL on the way to learning H.
      const HOOD_AURA_DPS = 35;
      // Per-tick state for the virginUlt chase-edge bot.
      const VIRGIN_BOT_SPEED  = 9.5;      // way faster than the player's ~5 → uncatchable without Q
      const VIRGIN_BOT_TARGET = 10.0;     // stays well beyond the Virgin donut's outer edge
      const VIRGIN_BOT_HYS    = 1.5;      // hysteresis band so it doesn't jitter
      // Soft arena bounds — virginUlt bot keeps itself east of the
      // rest of the stages so a stray collision during stage 4 can't
      // accidentally complete stage 5.
      const X_MIN = 38, X_MAX = 62;
      const Z_MIN = 28, Z_MAX = 48;
      // Pulse the danger disc + outline opacity so the zone reads as
      // "hot" — only when the bot is revealed (stage 4 active).
      if (dangerDisc.visible) {
        const pulse = 0.5 + 0.5 * Math.sin(tSec * 5);
        (dangerDisc.material as THREE.MeshBasicMaterial).opacity = 0.22 + 0.18 * pulse;
        (dangerRing.material as THREE.MeshBasicMaterial).opacity = 0.7 + 0.25 * pulse;
      }
      for (const d of dummies) {
        if (d.dead) continue;
        // Mog dummy regens so lifesteal loop is meaningful.
        if (d.regenPerSec > 0) {
          d.hp = Math.min(d.hpMax, d.hp + d.regenPerSec * dt);
        }
        // virginUlt: chase-edge AI. Sits ~6.5u from the player so a
        // Virgin's 5u donut JUST misses. Faster than the player at
        // baseline, so chasing without Ultra Instinct never lands a
        // hit. Pop Q (slows this bot to ~0.3x) and you can close in.
        if (d.kind === 'virginUlt' && d.group.visible && ultraBotActive && playerPos) {
          const dx = playerPos.x - d.pos.x;
          const dz = playerPos.z - d.pos.z;
          const dist = Math.hypot(dx, dz) || 0.001;
          let vx = 0, vz = 0;
          if (dist < VIRGIN_BOT_TARGET - VIRGIN_BOT_HYS) {
            // Too close → flee directly away.
            vx = -dx / dist;
            vz = -dz / dist;
          } else if (dist > VIRGIN_BOT_TARGET + VIRGIN_BOT_HYS) {
            // Too far → close back to the edge.
            vx = dx / dist;
            vz = dz / dist;
          } else {
            // In the sweet zone → side-step (perpendicular) so it
            // looks alive.
            vx = -dz / dist;
            vz =  dx / dist;
          }
          // Light noise so the path doesn't look robotic.
          vx += (Math.random() - 0.5) * 0.25;
          vz += (Math.random() - 0.5) * 0.25;
          const norm = Math.hypot(vx, vz) || 0.001;
          vx /= norm; vz /= norm;
          const speed = VIRGIN_BOT_SPEED * ultraSlow;
          d.pos.x = Math.max(X_MIN, Math.min(X_MAX, d.pos.x + vx * speed * dt));
          d.pos.z = Math.max(Z_MIN, Math.min(Z_MAX, d.pos.z + vz * speed * dt));
          d.group.position.copy(d.pos);
        }
        // virginRing chad bot: aura damage to the player when they're
        // inside HOOD_AURA_R AND not hooded. Damage clamped so the
        // player can't actually die during the demo — the point is
        // the FEEL of "ouch, vanish to escape", not a death scene.
        if (
          d.kind === 'virginRing' &&
          d.group.visible &&
          !playerHooded &&
          playerPos
        ) {
          const dx = playerPos.x - d.pos.x;
          const dz = playerPos.z - d.pos.z;
          if (dx * dx + dz * dz <= HOOD_AURA_R2) {
            playerDamage += HOOD_AURA_DPS * dt;
          }
        }
        // Idle bob — bob body AND head by the same delta so they stay
        // attached. Earlier code only nudged children[0] (body), which
        // left the head floating above a dipping torso. Shadow disc
        // (children[2]) stays glued to the ground at y=0.03.
        const bob = Math.sin(tSec * 3 + d.id) * 0.12;
        const body = d.group.children[0];
        const head = d.group.children[1];
        if (body) body.position.y = 1.2 + bob;
        if (head) head.position.y = 2.5 + bob;
      }
      return { playerDamage };
    },
    tryHitCircle(x: number, z: number, radius: number): Dummy[] {
      const r2 = radius * radius;
      const hits: Dummy[] = [];
      for (const d of dummies) {
        if (d.dead) continue;
        const dx = d.pos.x - x;
        const dz = d.pos.z - z;
        if (dx * dx + dz * dz <= r2) hits.push(d);
      }
      return hits;
    },
    hitDummy(id: number, dmg: number): { killed: boolean; leeched: number } {
      const d = dummies.find((x) => x.id === id);
      if (!d || d.dead) return { killed: false, leeched: 0 };
      d.hp = Math.max(0, d.hp - dmg);
      const leeched = Math.min(dmg, 18); // flat lifesteal payout for the mog stage
      if (d.hp <= 0) {
        d.dead = true;
        d.group.visible = false;
        return { killed: true, leeched };
      }
      return { killed: false, leeched };
    },
    revealVirginBranch(): void {
      // Called when stage 4 begins — reveal ONLY the virginRing
      // zone + dummy. The virginUlt zone + chase bot stay hidden
      // until stage 5 so the player can't see them ahead of time
      // and accidentally trigger them while on stage 4.
      virginRingMarker.visible = true;
      for (const d of dummies) {
        if (d.kind === 'virginRing' && !d.dead) d.group.visible = true;
      }
      // Light up the chad bot's red danger aura — telegraphs the
      // damage zone the player must avoid (or vanish through).
      dangerDisc.visible = true;
      dangerRing.visible = true;
    },
    activateUltraBot(): void {
      // Stage 5 enter — reveal the virginUlt zone + dummy, then
      // start the chase AI. Before this point the bot is invisible
      // and immobile, so stage 4 can't bleed into stage 5.
      virginUltMarker.visible = true;
      for (const d of dummies) {
        if (d.kind === 'virginUlt' && !d.dead) d.group.visible = true;
      }
      ultraBotActive = true;
      // Stage 4 is over — hide the chad bot's danger aura so the
      // player can navigate stage 5 without a leftover red zone
      // telegraphing a threat that no longer applies.
      dangerDisc.visible = false;
      dangerRing.visible = false;
    },
    dispose() {
      parent.remove(group);
      for (const d of dummies) {
        d.group.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.geometry) m.geometry.dispose();
          const mat = (m.material as THREE.Material | undefined);
          if (mat) mat.dispose();
        });
      }
      ground.geometry.dispose();
      (ground.material as THREE.Material).dispose();
    },
  };
}

function addVirginRangeDemo(parent: THREE.Object3D, pos: THREE.Vector3): void {
  // Outer ring — Virgin's max reach (5u).
  const outer = new THREE.Mesh(
    new THREE.RingGeometry(4.92, 5.0, 64),
    new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
  );
  outer.rotation.x = -Math.PI / 2;
  outer.position.set(pos.x, 0.07, pos.z);
  parent.add(outer);
  // Inner ring — Virgin's dead-zone floor (4u). Dashed via segmented
  // ring geometry approximated with a thin solid ring (good enough
  // for a demo).
  const inner = new THREE.Mesh(
    new THREE.RingGeometry(3.94, 4.0, 64),
    new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.55, side: THREE.DoubleSide }),
  );
  inner.rotation.x = -Math.PI / 2;
  inner.position.set(pos.x, 0.07, pos.z);
  parent.add(inner);
  // Donut fill so the band reads as the damage zone.
  const band = new THREE.Mesh(
    new THREE.RingGeometry(4.0, 5.0, 64),
    new THREE.MeshBasicMaterial({
      color: 0x60a5fa, transparent: true, opacity: 0.18,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    }),
  );
  band.rotation.x = -Math.PI / 2;
  band.position.set(pos.x, 0.06, pos.z);
  parent.add(band);
  // Pixel-style Virgin body at the ring center — simple blue box +
  // white head so it reads as "another player" without pulling in
  // the AnimatedSprite pipeline.
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.0, 1.8, 1.0),
    new THREE.MeshStandardMaterial({ color: 0x60a5fa, emissive: 0x60a5fa, emissiveIntensity: 0.35, roughness: 0.6 }),
  );
  body.position.set(pos.x, 1.0, pos.z);
  parent.add(body);
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.4, 12, 10),
    new THREE.MeshStandardMaterial({ color: 0xf3f4ec, emissive: 0xf3f4ec, emissiveIntensity: 0.15 }),
  );
  head.position.set(pos.x, 2.2, pos.z);
  parent.add(head);
  // Label sprite above — "VIRGIN RANGE".
  const c = document.createElement('canvas');
  c.width = 640; c.height = 160;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = 'rgba(15,23,42,.92)';
  roundRect(ctx, 12, 12, c.width - 24, c.height - 24, 26);
  ctx.fill();
  ctx.strokeStyle = '#60a5fa';
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.shadowColor = 'rgba(96,165,250,.75)';
  ctx.shadowBlur = 18;
  ctx.fillStyle = '#60a5fa';
  ctx.font = '900 56px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('🤓 VIRGIN RANGE', c.width / 2, 54);
  ctx.shadowBlur = 0;
  ctx.font = '700 26px system-ui, sans-serif';
  ctx.fillStyle = '#cbd5e1';
  ctx.fillText('donut 4 – 5 u', c.width / 2, 96);
  ctx.font = '700 22px system-ui, sans-serif';
  ctx.fillStyle = '#fde047';
  ctx.fillText('Q = ULTRA INSTINCT · 5s slow-time', c.width / 2, 132);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  label.scale.set(4.8, 1.2, 1);
  label.position.set(pos.x, 3.8, pos.z);
  parent.add(label);
}

function addZoneMarker(parent: THREE.Object3D, pos: THREE.Vector3, color: number, label: string): THREE.Group {
  // Group so the caller can toggle visibility (Virgin-branch zones
  // stay hidden until stage 5 starts).
  const g = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(1.4, 1.8, 48),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(pos.x, 0.06, pos.z);
  g.add(ring);

  // Pulse disc under the ring.
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(2.2, 32),
    new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.25,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.set(pos.x, 0.04, pos.z);
  g.add(glow);

  // Label sprite floating above.
  const c = document.createElement('canvas');
  c.width = 512; c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = 'rgba(15,23,42,.9)';
  roundRect(ctx, 12, 12, c.width - 24, c.height - 24, 24);
  ctx.fill();
  ctx.strokeStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.fillStyle = ctx.strokeStyle;
  ctx.font = '900 52px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = ctx.strokeStyle as string;
  ctx.shadowBlur = 14;
  ctx.fillText(label, c.width / 2, c.height / 2 + 2);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.scale.set(3.6, 0.9, 1);
  sprite.position.set(pos.x, 3.8, pos.z);
  g.add(sprite);

  parent.add(g);
  return g;
}

function makeDummyMesh(kind: DummyKind): THREE.Group {
  const g = new THREE.Group();
  // Stage 4 (virginRing) and 5 (virginUlt) are the Virgin tutorial
  // stages — narratively the player IS Virgin, so the targets are
  // CHAD bots (orange). Same color as the in-match Chad faction so
  // the visual mapping is "I'm the Virgin, those are the Chads I'm
  // hunting".
  const color =
    kind === 'attack'     ? 0xef4444 :
    kind === 'mog'        ? 0xfb923c :
    kind === 'leap'       ? 0x38bdf8 :
    kind === 'virginRing' ? 0xfb923c :
    kind === 'virginUlt'  ? 0xfb923c :
                            0xfb923c;
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 2.0, 1.2),
    new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.35, roughness: 0.6 }),
  );
  body.position.y = 1.2;
  g.add(body);
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.45, 12, 10),
    new THREE.MeshStandardMaterial({ color: 0xf3f4ec, emissive: 0xf3f4ec, emissiveIntensity: 0.2 }),
  );
  head.position.y = 2.5;
  g.add(head);
  // Shadow disc.
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(0.7, 16),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.03;
  g.add(shadow);
  return g;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
